import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
    PencilAltIcon,
    EllipsisVIcon,
    UserIcon,
    TrashIcon
} from '@patternfly/react-icons';
import { getAdStateConfig, getAdTypeConfig, isActiveGenerationStep } from './adStateUtils';
import { GenerationProgress } from './GenerationProgress';

export interface IAdCardProps {
    ad: {
        adId: string;
        name: string;
        organizationName?: string;
        organizationId?: string;
        creatorName?: string;
        imageUrl?: string;
        presignedUrl?: string;
        thumbnailUrl?: string;
        currentState?: string;
        currentGenerationStep?: string;
        status?: string;
        adType?: string;
        tags?: string[];
        updateDateTime?: string;
        media?: { uri?: string };
        failureReason?: string;
    };
    onEditClick?: (ad: any) => void;
    onCardClick?: (ad: any) => void;
    onDeleteClick?: (ad: any) => void;
    onReviewClick?: (ad: any) => void;
}

const AdCard: React.FunctionComponent<IAdCardProps> = ({ ad, onEditClick, onCardClick, onDeleteClick, onReviewClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    const isInProgress = ad.currentState === 'IN_PROGRESS' || ad.currentState === 'IN_PROGESS';

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return function() {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Generate a unique gradient based on ad name for placeholder
    const getGradientFromName = (name: string) => {
        const gradients = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
            'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
            'linear-gradient(135deg, #667eea 0%, #f093fb 100%)',
            'linear-gradient(135deg, #5ee7df 0%, #b490ca 100%)'
        ];
        // Simple hash from name to pick a gradient
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return gradients[Math.abs(hash) % gradients.length];
    };

    const [thumbnailFailed, setThumbnailFailed] = useState(false);

    // Preload thumbnail to detect if it exists; fall back to full image on error
    useEffect(() => {
        setThumbnailFailed(false);
        if (ad.thumbnailUrl) {
            const img = new window.Image();
            img.src = ad.thumbnailUrl;
            img.onerror = () => setThumbnailFailed(true);
        }
    }, [ad.thumbnailUrl]);

    const mediaUrl = (!thumbnailFailed && ad.thumbnailUrl) || ad.presignedUrl || ad.imageUrl;
    const hasImage = mediaUrl && mediaUrl.trim() !== '';
    const placeholderGradient = getGradientFromName(ad.name || 'Ad');
    const adInitial = (ad.name || 'A').charAt(0).toUpperCase();

    const stateConfig = getAdStateConfig(ad.currentState || ad.status);
    const typeConfig = getAdTypeConfig(ad.adType);

    const styles = {
        cardContainer: {
            position: 'relative' as const,
            aspectRatio: '4 / 3',
            borderRadius: '12px',
            overflow: 'hidden',
            cursor: 'pointer',
            transform: isHovered ? 'scale(1.02)' : 'scale(1)',
            boxShadow: isInProgress
                ? undefined  // handled by animation
                : isHovered
                    ? '0 20px 40px rgba(0, 0, 0, 0.2)'
                    : '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            animation: isInProgress ? 'adCardGlow 2s ease-in-out infinite' : undefined
        },
        backgroundImage: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: hasImage ? `url(${mediaUrl})` : placeholderGradient,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'transform 0.3s ease',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)'
        },
        placeholderInitial: {
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '80px',
            fontWeight: 800,
            color: 'rgba(255, 255, 255, 0.25)',
            textTransform: 'uppercase' as const,
            userSelect: 'none' as const,
            pointerEvents: 'none' as const
        },
        gradientOverlay: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 40%, rgba(0,0,0,0.7) 100%)',
            transition: 'background 0.3s ease'
        },
        content: {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column' as const,
            justifyContent: 'space-between',
            padding: '16px',
            boxSizing: 'border-box' as const
        },
        topRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
        },
        badgeContainer: {
            display: 'flex',
            gap: '6px',
            flexWrap: 'wrap' as const
        },
        statusBadge: {
            backgroundColor: stateConfig.color.bg,
            color: stateConfig.color.text,
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.5px'
        },
        typeBadge: {
            backgroundColor: typeConfig.color.bg,
            color: typeConfig.color.text,
            padding: '4px 10px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 600
        },
        actionButtons: {
            display: 'flex',
            gap: '8px',
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'all 0.3s ease'
        },
        actionButton: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            border: 'none',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#374151',
            transition: 'all 0.2s ease',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        },
        menuContainer: {
            position: 'relative' as const
        },
        dropdownMenu: {
            position: 'absolute' as const,
            top: '100%',
            right: 0,
            marginTop: '4px',
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            zIndex: 100,
            minWidth: '140px'
        },
        menuItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            fontSize: '14px',
            color: '#374151',
            cursor: 'pointer',
            transition: 'background 0.2s',
            border: 'none',
            background: 'none',
            width: '100%',
            textAlign: 'left' as const
        },
        menuItemDanger: {
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 16px',
            fontSize: '14px',
            color: '#dc2626',
            cursor: 'pointer',
            transition: 'background 0.2s',
            border: 'none',
            background: 'none',
            width: '100%',
            textAlign: 'left' as const
        },
        bottomContent: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '4px'
        },
        adName: {
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: 700,
            margin: 0,
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        },
        organizationName: {
            color: 'rgba(255, 255, 255, 0.85)',
            fontSize: '13px',
            fontWeight: 500,
            margin: 0
        },
        creatorInfo: {
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: 'rgba(255, 255, 255, 0.75)',
            fontSize: '12px',
            marginTop: '8px',
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'translateY(0)' : 'translateY(10px)',
            transition: 'all 0.3s ease'
        },
        failureReasonBanner: {
            backgroundColor: 'rgba(220, 38, 38, 0.85)',
            borderRadius: '6px',
            padding: '6px 10px',
            fontSize: '11px',
            lineHeight: 1.4,
            color: '#ffffff',
            marginBottom: '4px',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' as const
        },
        // Delete confirmation modal styles
        confirmOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
        },
        confirmModal: {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        },
        confirmTitle: {
            fontSize: '18px',
            fontWeight: 700,
            color: '#1f2937',
            margin: '0 0 8px 0'
        },
        confirmText: {
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 20px 0',
            lineHeight: 1.5
        },
        confirmButtons: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
        },
        cancelButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 500,
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            background: '#ffffff',
            color: '#374151',
            cursor: 'pointer'
        },
        deleteButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            border: 'none',
            borderRadius: '8px',
            background: '#dc2626',
            color: '#ffffff',
            cursor: 'pointer'
        }
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onEditClick) {
            onEditClick(ad);
        }
    };

    const handleCardClick = () => {
        if (onCardClick) {
            onCardClick(ad);
        }
    };

    const handleMenuClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(!showMenu);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
        if (onDeleteClick) {
            onDeleteClick(ad);
        }
    };

    const handleCancelDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
    };

    return (
        <>
            {isInProgress && (
                <style>{`
                    @keyframes adCardGlow {
                        0%, 100% {
                            box-shadow: 0 0 15px rgba(139, 92, 246, 0.4), 0 0 30px rgba(139, 92, 246, 0.2), 0 0 60px rgba(139, 92, 246, 0.1);
                        }
                        50% {
                            box-shadow: 0 0 20px rgba(139, 92, 246, 0.6), 0 0 45px rgba(139, 92, 246, 0.35), 0 0 80px rgba(139, 92, 246, 0.15);
                        }
                    }
                `}</style>
            )}
            <div
                style={styles.cardContainer}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => { setIsHovered(false); setShowMenu(false); }}
                onClick={handleCardClick}
            >
                {/* Background Image or Gradient */}
                <div style={styles.backgroundImage} />

                {/* Large Initial Letter for Placeholder */}
                {!hasImage && <div style={styles.placeholderInitial}>{adInitial}</div>}

                {/* Gradient Overlay */}
                <div style={styles.gradientOverlay} />

                {/* Content */}
                <div style={styles.content}>
                    {/* Top Row - Badges & Action Buttons */}
                    <div style={styles.topRow}>
                        <div style={styles.badgeContainer}>
                            <span style={styles.statusBadge}>
                                {stateConfig.label}
                            </span>
                            {ad.adType && (
                                <span style={styles.typeBadge}>
                                    {typeConfig.label}
                                </span>
                            )}
                        </div>
                        <div style={styles.actionButtons}>
                            <button
                                style={styles.actionButton}
                                onClick={handleEditClick}
                                title="Edit"
                            >
                                <PencilAltIcon />
                            </button>
                            <div style={styles.menuContainer} ref={menuRef}>
                                <button
                                    style={styles.actionButton}
                                    onClick={handleMenuClick}
                                    title="More options"
                                >
                                    <EllipsisVIcon />
                                </button>
                                {showMenu && (
                                    <div style={styles.dropdownMenu}>
                                        <button
                                            style={styles.menuItem}
                                            onClick={handleEditClick}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                        >
                                            <PencilAltIcon /> Edit
                                        </button>
                                        <button
                                            style={styles.menuItemDanger}
                                            onClick={handleDeleteClick}
                                            onMouseEnter={(e) => e.currentTarget.style.background = '#fef2f2'}
                                            onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                        >
                                            <TrashIcon /> Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Bottom Content - Name, Organization, Creator */}
                    <div style={styles.bottomContent}>
                        {/* Generation Progress for IN_PROGRESS ads */}
                        {isInProgress && ad.currentGenerationStep && isActiveGenerationStep(ad.currentGenerationStep) && (
                            <GenerationProgress currentStep={ad.currentGenerationStep} />
                        )}
                        {/* Failure reason banner for failed ads */}
                        {ad.failureReason && (ad.currentState === 'FAILED' || ad.currentState === 'MODERATION_FAILED') && (
                            <div style={styles.failureReasonBanner}>{ad.failureReason}</div>
                        )}
                        <h3 style={styles.adName}>{ad.name}</h3>
                        <p style={styles.organizationName}>
                            {ad.organizationName || `Organization ${ad.organizationId}`}
                        </p>
                        <div style={styles.creatorInfo}>
                            <UserIcon />
                            <span>{ad.creatorName || 'Unknown creator'}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div style={styles.confirmOverlay} onClick={handleCancelDelete}>
                    <div style={styles.confirmModal} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.confirmTitle}>Delete Ad</h3>
                        <p style={styles.confirmText}>
                            Are you sure you want to delete "{ad.name}"? This action cannot be undone.
                        </p>
                        <div style={styles.confirmButtons}>
                            <button style={styles.cancelButton} onClick={handleCancelDelete}>
                                Cancel
                            </button>
                            <button style={styles.deleteButton} onClick={handleConfirmDelete}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

const MemoizedAdCard = React.memo(AdCard, (prev, next) => {
    const p = prev.ad;
    const n = next.ad;

    // If thumbnailUrl is null, don't skip re-render — it may get populated
    if (!p.thumbnailUrl || !n.thumbnailUrl) return false;

    return (
        p.media?.uri === n.media?.uri &&
        p.currentState === n.currentState &&
        p.currentGenerationStep === n.currentGenerationStep &&
        p.name === n.name &&
        p.adType === n.adType &&
        p.organizationName === n.organizationName &&
        p.creatorName === n.creatorName &&
        p.failureReason === n.failureReason
    );
});

export { MemoizedAdCard as AdCard };
